/**
 * Mobile-only settings menu (Iter 67dp).
 *
 * Shown in the Header on `< md` viewports as a single Globe icon button.
 * Tapping it opens a dropdown panel containing the Language + Currency
 * selectors (which previously sat exposed in the header and ate horizontal
 * room on phones, truncating the brand wordmark + Sign-up button).
 *
 * On `md+` the LanguageSelector / CurrencySelector are rendered inline in
 * the Header as before — this component is hidden there.
 */
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/lib/i18n";
import { switchLocaleUrl } from "@/lib/localeRoutes";
import { useCurrency } from "@/lib/currency";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Globe, Check } from "lucide-react";

const CURRENCY_FLAGS = {
  USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧", CAD: "🇨🇦", AUD: "🇦🇺",
  INR: "🇮🇳", AED: "🇦🇪", JPY: "🇯🇵", CHF: "🇨🇭", SGD: "🇸🇬", ALL: "🇦🇱",
};

export default function MobileSettingsMenu() {
  const { i18n, t } = useTranslation();
  const { currency, setCurrency, supported } = useCurrency();
  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  // Iter 67dz — hard navigate so the locale URL prefix takes effect.
  const handleSwitch = (code) => {
    if (code === currentLang.code) return;
    window.location.assign(switchLocaleUrl(code));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-full border border-border bg-white hover:bg-neutral-50 transition shrink-0"
        data-testid="mobile-settings-trigger"
        aria-label="Language and currency"
      >
        <Globe className="h-4 w-4 text-neutral-700" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 max-h-[70vh] overflow-y-auto">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.16em] text-neutral-500 font-bold">
          {t("header.languageLabel")}
        </DropdownMenuLabel>
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleSwitch(lang.code)}
            className="cursor-pointer"
            data-testid={`mobile-lang-option-${lang.code}`}
          >
            <span className="mr-2 text-base leading-none">{lang.flag}</span>
            <span className="flex-1 text-sm">{lang.name}</span>
            {lang.beta && (
              <span
                className="mr-2 text-[9px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-1.5 py-px"
                data-testid={`mobile-language-beta-badge-${lang.code}`}
              >
                Beta
              </span>
            )}
            {currentLang.code === lang.code && <Check className="h-3.5 w-3.5 text-amber-600" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.16em] text-neutral-500 font-bold">
          Display currency
        </DropdownMenuLabel>
        {(supported || []).map((c) => (
          <DropdownMenuItem
            key={c}
            onClick={() => setCurrency(c)}
            className="cursor-pointer"
            data-testid={`mobile-currency-option-${c}`}
          >
            <span className="mr-2 text-base leading-none w-5 inline-block" aria-hidden="true">{CURRENCY_FLAGS[c] || "🌐"}</span>
            <span className="flex-1 text-sm">{c}</span>
            {currency === c && <Check className="h-3.5 w-3.5 text-amber-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
